import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/axios';
import toast from 'react-hot-toast';

export default function AdminBlogs() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = () => {
    setLoading(true);
    api.get('/blogs/admin/all')
      .then((res) => setBlogs(res.data))
      .catch((err) => toast.error('Failed to load blog posts'))
      .finally(() => setLoading(false));
  };

  const handleTogglePublish = async (blog) => {
    try {
      await api.put(`/blogs/${blog._id}`, { isPublished: !blog.isPublished });
      toast.success(`Blog ${!blog.isPublished ? 'published' : 'unpublished'} successfully!`);
      fetchBlogs();
    } catch (err) {
      toast.error('Failed to update blog status');
    }
  };

  const handleDelete = async (id, title) => {
    if (window.confirm(`Are you sure you want to delete "${title}"?`)) {
      try {
        await api.delete(`/blogs/${id}`);
        toast.success('Blog post deleted successfully');
        setBlogs(blogs.filter((b) => b._id !== id));
      } catch (err) {
        toast.error('Failed to delete blog post');
      }
    }
  };

  const filtered = blogs.filter((b) =>
    b.title.toLowerCase().includes(search.toLowerCase()) ||
    b.category.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header Action Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-[#E8DFD8] shadow-sm">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#2B1810]">Blog Posts Management</h1>
          <p className="text-xs text-[#8A796C]">Publish spiritual guides, authenticity articles, and temple care stories</p>
        </div>

        <Link
          to="/admin/blogs/new"
          className="bg-[#C49A45] hover:bg-[#B08736] text-white font-bold text-xs uppercase tracking-wider px-5 py-2.5 rounded-lg shadow transition-all flex items-center gap-2"
        >
          <span>+ Create New Blog Post</span>
        </Link>
      </div>

      {/* Search & Filter Bar */}
      <div className="bg-white p-4 rounded-xl border border-[#E8DFD8] shadow-sm flex items-center justify-between gap-4">
        <input
          type="text"
          placeholder="Search by title or category..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input-field max-w-sm py-1.5 text-xs"
        />
        <span className="text-xs text-[#8A796C] font-bold">
          Total Posts: {filtered.length}
        </span>
      </div>

      {/* Blog Posts Table */}
      <div className="bg-white rounded-xl border border-[#E8DFD8] shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-xs text-[#8A796C]">Loading blog posts...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-xs text-[#8A796C]">No blog posts found. Create your first post!</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-[#FAF8F5] border-b border-[#E8DFD8] text-[#2B1810] font-bold uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="p-4">Cover</th>
                  <th className="p-4">Blog Title</th>
                  <th className="p-4">Category</th>
                  <th className="p-4">Author</th>
                  <th className="p-4">Status</th>
                  <th className="p-4">Views</th>
                  <th className="p-4">Date</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8DFD8] text-[#2B1810]">
                {filtered.map((blog) => (
                  <tr key={blog._id} className="hover:bg-[#FAF8F5]">
                    <td className="p-4">
                      <img
                        src={blog.image}
                        alt={blog.title}
                        className="w-12 h-10 object-cover rounded border border-[#E8DFD8]"
                        onError={(e) => {
                          e.target.src = '/assets/hero_perfect_ganesha.jpg';
                        }}
                      />
                    </td>
                    <td className="p-4 font-bold text-[#2B1810] max-w-xs truncate">
                      <Link to={`/blog/${blog.slug}`} target="_blank" className="hover:text-[#C49A45]">
                        {blog.title}
                      </Link>
                    </td>
                    <td className="p-4 text-[#8A796C] font-semibold">{blog.category}</td>
                    <td className="p-4 text-[#8A796C]">{blog.author}</td>
                    <td className="p-4">
                      <button
                        onClick={() => handleTogglePublish(blog)}
                        className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${
                          blog.isPublished ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {blog.isPublished ? '✓ Published' : 'Draft'}
                      </button>
                    </td>
                    <td className="p-4 font-bold text-[#8A796C]">{blog.viewsCount}</td>
                    <td className="p-4 text-[#8A796C]">
                      {new Date(blog.createdAt).toLocaleDateString()}
                    </td>
                    <td className="p-4 text-right space-x-2">
                      <Link
                        to={`/admin/blogs/${blog._id}`}
                        className="px-3 py-1 bg-[#FAF8F5] border border-[#E8DFD8] hover:border-[#C49A45] hover:text-[#C49A45] rounded font-bold transition-colors"
                      >
                        Edit
                      </Link>
                      <button
                        onClick={() => handleDelete(blog._id, blog.title)}
                        className="px-3 py-1 bg-red-50 border border-red-200 text-red-700 hover:bg-red-100 rounded font-bold transition-colors"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
